"use client";

import { createPath } from "@/service/routing/routing";
import { CardResidencyDetails } from "@/components/doctor-training/doctor-training-card/preset/card-residency-details";
import { CardInternshipDetails } from "@/components/doctor-training/doctor-training-card/preset/card-internship-details";
import { cn } from "@/lib/utils";
import {
  Card as CardComponent,
  CardContent,
  CardHeader,
} from "@/components/ui/card";
import { MapPinIcon, Trash2Icon } from "lucide-react";
import { clientGet } from "@/service/client-api/client-api";
import { useContext } from "react";
import { ResourceContext } from "@/contexts/resource-context";

export const DoctorTrainingCard = ({
  facility,
  type,
  collapsed = true,
}: {
  facility: MedicalFacilityCard;
  type: DoctorTrainingType;
  collapsed?: boolean;
}) => {
  const { title, residencyDetails, internshipDetails, city } = facility;
  const details = type === "residency" ? residencyDetails : internshipDetails;
  let link = "";
  const { favouriteList } = useContext(ResourceContext);

  if (type === "residency") {
    const unitGroups = residencyDetails.unitGroups;
    if (unitGroups.length === 1 && unitGroups[0].units.length === 1) {
      link = createPath("residency_map.facility.unit.index", {
        facilitySlug: facility.slug,
        unitSlug: unitGroups[0].units[0].slug,
      });
    } else {
      link = createPath("residency_map.facility.index", {
        slug: facility.slug,
      });
    }
  } else {
    link = createPath("internship_map.facility.index", {
      slug: facility.slug,
    });
  }

  const typeColors: Record<MedicalFacilityType["key"], string> = {
    clinical: "text-facility-clinical",
    "non-clinical": "text-facility-non-clinical",
    military: "text-facility-military",
    "health-center": "text-facility-health-center",
  };

  return (
    <CardComponent
      className={cn(`rounded-lg border shadow-none transition-all`)}
    >
      <CardHeader>
        <div className={"flex items-start justify-between"}>
          <div
            className={cn(
              `${
                typeColors[facility.type.key]
              } mb-4 text-sm font-semibold uppercase`,
            )}
          >
            {facility.type.title}
          </div>
          {favouriteList && type === "internship" && (
            <div
              onClick={async () => {
                await clientGet("favourite", {
                  resourceType: "medical_facility",
                  resourceId: facility.id,
                  action: "remove",
                });
                window.location.reload();
              }}
              className={"-mt-2 cursor-pointer p-2 text-rose-600"}
            >
              <Trash2Icon size={"16px"} />
            </div>
          )}
        </div>

        <div className={"flex justify-between"}>
          <a
            href={link}
            className={`text-remedium-blue-hover text-base leading-normal font-semibold text-zinc-950`}
            target={"_blank"}
          >
            {title}
          </a>
        </div>
        {type === "residency" && (
          <div
            className={
              "mt-3 flex items-center gap-2 text-sm leading-tight text-[#1e1926]"
            }
          >
            <MapPinIcon size={"16px"} />
            {city}
          </div>
        )}
      </CardHeader>
      <CardContent>
        {type === "residency" ? (
          <CardResidencyDetails
            details={details as MedicalFacilityCardResidencyDetails}
            facility={facility}
            collapsed={collapsed}
          />
        ) : (
          <CardInternshipDetails
            details={details as MedicalFacilityCardInternshipDetails}
            facility={facility}
            city={city}
          />
        )}
      </CardContent>
    </CardComponent>
  );
};
